import type { PluginWithOptions } from 'markdown-it'
import MarkdownIt from 'markdown-it'
import mila from 'markdown-it-link-attributes'

import { memoize } from '../memoize'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LinkAttributes = { matcher?: (href: string, config: any) => boolean, attrs: Record<string, string> }

export type MarkdownRendererOptions = {
  /**
   * Configures [markdown-it-link-attributes](https://www.npmjs.com/package/markdown-it-link-attributes)
   * with the provided configuration.
   *
   * See [Basic Configuration](https://github.com/crookedneighbor/markdown-it-link-attributes#basic-configuration)
   * for further instructions on how to use this prop
   */
  linksAttributes?: LinkAttributes,
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) breaks parameter
   *
   * Convert '\n' in paragraphs into `<br>`
   */
  breaks?: boolean
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) html parameter
   *
   * Enable HTML tags in source `markdown` text
   */
  html?: boolean
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) langPrefix parameter
   * CSS language prefix for fenced blocks. Can be useful for external highlighters.
   */
  langPrefix?: string
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) linkify parameter
   *
   * Autoconvert URL-like text to links
   */
  linkify?: boolean

  /**
   * An array of objects like `{plugin: PluginWithOptions, config: unknown}` to
   * use with [markdown-it use() function](https://github.com/markdown-it/markdown-it#plugins-load)
   */
  additionalPlugins?: { plugin: PluginWithOptions, config: unknown }[],

  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) quotes parameter
   *
   * Double + single quotes replacement pairs, when typographer enabled,
   * and smartquotes on. Could be either a String or an Array.
   *
   * For example, you can use `'«»„“'` for Russian, `'„“‚‘'` for German,
   * and `['«\xA0', '\xA0»', '‹\xA0', '\xA0›']` for French (including nbsp).
   */
  quotes?: string | string[]
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) typographer parameter
   *
   * Enable some language-neutral replacement + quotes beautification
   *
   * For the full list of replacements, see [here](https://github.com/markdown-it/markdown-it/blob/master/lib/rules_core/replacements.js)
   */
  typographer?: boolean
  /**
   * [markdown-it](https://github.com/markdown-it/markdown-it) xhtmlOut parameter
   *
   * Use '/' to close single tags (`<br />`).
   */
  xhtmlOut?: boolean
}

export type RenderMarkdownOptions = MarkdownRendererOptions & {
  /**
   * Renders `markdown` with no wrapper paragraph tags `<p></p>`
   *
   * Use this mode when your markdown doesn't need to represent multiple lines of text.
   */
  inline?: boolean
}

export const MARKDOWN_DEFAULTS = {
  linksAttributes: {
    attrs: {
      target: '_blank',
      rel: 'noopener'
    }
  },
  breaks: false,
  html: false,
  langPrefix: 'language-',
  linkify: false,
  inline: false,
  additionalPlugins: [],
  quotes: '“”‘’',
  typographer: true,
  xhtmlOut: false
}

export const getMarkdownRenderer = memoize((options?: MarkdownRendererOptions) => {
  const opts = { ...MARKDOWN_DEFAULTS, ...options }
  const markdown = new MarkdownIt()
    .use(mila, opts.linksAttributes)
    .set({
      breaks: opts.breaks,
      html: opts.html,
      langPrefix: opts.langPrefix,
      linkify: opts.linkify,
      quotes: opts.quotes,
      typographer: opts.typographer,
      xhtmlOut: opts.xhtmlOut
    })

  opts.additionalPlugins.forEach(plg => {
    markdown.use(plg.plugin, plg.config)
  })

  return markdown
})

export const renderMarkdown = (text:string, options: RenderMarkdownOptions = {}) => {
  const { inline = MARKDOWN_DEFAULTS.inline, ...opts } = options
  const markdown = getMarkdownRenderer(opts)

  return inline ? markdown.renderInline(text) : markdown.render(text)
}
